import { Autocomplete, TextField, Tooltip } from "@mui/material";
import React from "react";
import { matchSorter } from 'match-sorter';
export default function SelectableSearch(props) {
  const [open, setOpen] = React.useState(false);
  const loading = open && props?.data?.values?.length === 0 ? true : false;
  var optionLabel = props?.fieldLabel || "optionname"

  React.useEffect(() => {
    let active = true;
    if (!loading) {
      return undefined;
    }
    if (active) {
      if (typeof props?.loading === 'function') {
        props.loading();
      }
    }
    return () => {
      active = false;
    };
    //eslint-disable-next-line
  }, [loading, props?.data?.value]);

  const filterOptions = (options, { inputValue }) => {
    let vals = matchSorter(options, inputValue, { keys: props?.keys });
    return vals
  };

  const hasTooltipConfig = Array.isArray(props?.optionsTooltipKeys) && props.optionsTooltipKeys.length > 0;

  return (
    <React.Fragment>
      <Autocomplete
        multiple={props?.multiple}
        freeSolo={props?.disabled}
        size="small"
        limitTags={props?.limitTags || 3}
        disabled={props?.disabled}
        noOptionsText={props?.noOptionText || "No Options"}
        value={
          props?.multiple
            ? Array.isArray(props?.value)
              ? props?.value
              : []
            : props?.value || null
        }
        id={props?.id}
        name={props?.name}
        disableClearable={props?.disableClearable}
        openOnFocus
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        onChange={(event, value) => props?.onChangeEvent(props?.name, value)}
        filterSelectedOptions
        getOptionLabel={(option) => {
          if (option && option?.[optionLabel]) {
            return option?.[optionLabel];
          } else {
            return "";
          }
        }}
        options={
          props?.data && props?.data?.values?.length > 0 ? props?.data?.values : []
        }
        loading={props?.data?.load}
        renderInput={(params) => (
          <TextField
            {...params}
            label={props?.label}
            placeholder={props?.placeholder} 
            autoFocus={props?.autoFocus}
            required={props?.required}
            variant={props?.variantType ? props?.variantType : "outlined"}
            name={props?.name}
            onBlur={props?.onBlur}
            error={props?.error}
            size={props?.size}
            helperText={props?.helperText}
            InputLabelProps={{
              ...(props?.placeholder ? { shrink: true } : {}),
              ...params.InputLabelProps,
              ...props?.InputLabelProps,
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {props?.endIcon}
                  {params.InputProps?.endAdornment}
                </React.Fragment>
              ),
            }}
             sx={{
               ...(props?.customBackground  && {
                "& .MuiOutlinedInput-root": {
                  backgroundColor: props?.disabled ? "inherit" : "#ffffff",
                },
                "& .MuiOutlinedInput-root.Mui-disabled": {
                  backgroundColor: "inherit",
                },
              }),
              ...props?.styles,
            }}
          />
        )}
        {...(props?.filterOptions && { filterOptions: filterOptions })}
        {...((props?.renderOption || hasTooltipConfig) && {
          renderOption: props?.renderOption || ((renderProps, option) => defaultRenderOption(renderProps, option, props, optionLabel)),
        })}
        {...(props?.isOptionEqualToValue && { isOptionEqualToValue: props?.isOptionEqualToValue })}
        {...(props?.grouping ? { groupBy: props?.groupByFn || ((option) => option?.user_type) } : {})}
      />
    </React.Fragment>
  );
}

/**
 * Generates tooltip text for an option based on props.optionsTooltipKeys.
 * 
 * Examples for `props.optionsTooltipKeys`:
 * 
 * 1. Array of strings (using option keynames and static text in between):
 *    optionsTooltipKeys={["previous_employee_id", " | Mobile: ", "personal_mobile"]}
 *    -> Given option: { previous_employee_id: "OH1005", personal_mobile: "7666554444" }
 *    -> Output: "OH1005 | Mobile: 7666554444"
 * 
 * 2. Array of key objects:
 *    optionsTooltipKeys={[
 *      { key: "previous_employee_id", prefix: "Emp ID: " },
 *      { key: "personal_mobile", prefix: "Mobile: " }
 *    ]}
 *    -> Output: "Emp ID: OH1005 | Mobile: 7666554444"
 */
const getTooltipText = (option, optionsTooltipKeys, tooltipSeparator) => {
  if (!option || !Array.isArray(optionsTooltipKeys)) return "";

  const parts = [];
  let hasKeyObjects = false;

  optionsTooltipKeys.forEach((item) => {
    if (typeof item === "string") {
      let val = option[item];
      if (typeof val === "boolean") {
        val = val ? "Yes" : "No";
      }
      if (val) {
        parts.push(String(val));
      } else if (!option.hasOwnProperty(item)) {
        parts.push(item);
      }
    } else if (item && typeof item === "object") {
      if (item.key) {
        hasKeyObjects = true;
        let val = option[item.key];
        if (typeof val === "boolean") {
          val = val ? "Yes" : "No";
        }
        if (val) {
          const prefix = item?.prefix || "";
          const suffix = item?.suffix || "";
          parts.push(`${prefix}${val}${suffix}`);
        }
      }
    }
  });

  if (parts?.length === 0) return "";

  const separator = tooltipSeparator !== undefined ? tooltipSeparator : (hasKeyObjects ? " | " : " ");
  return parts?.filter(Boolean)?.join(separator);
};

const defaultRenderOption = (renderProps, option, props, optionLabel) => {
  const tooltipText = getTooltipText(option, props?.optionsTooltipKeys, props?.tooltipSeparator);
  const { key, ...otherProps } = renderProps;

  const labelText = option && option[optionLabel] ? option[optionLabel] : "";

  const content = (
    <li key={key} {...otherProps}>
      {labelText}
    </li>
  );

  if (tooltipText) {
    return (
      <Tooltip key={key} title={tooltipText} arrow placement="left">
        {content}
      </Tooltip>
    );
  }

  return content;
};
